import React, {useEffect, useState} from "react";
import axios from "axios"
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Alert from 'react-bootstrap/Alert'
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faArrowRight, faDownload, faRetweet} from "@fortawesome/free-solid-svg-icons";
import {axiosErrorHandler} from "./common/util/AxiosUtil";
import FilterForm, {Filter} from "./FilterForm";
import {Spinner} from "react-bootstrap";
import FilterParamType from "./common/enum/FilterParamType";
import {extractFirstHeaderLink} from "./util/LinkHeaderUtil";
import ConsignmentTable from "./consignment/ConsignmentTable";
import ConsignmentList from "./consignment/ConsignmentList";
import API from "../services/API";
import {useAppSelector} from "../store";
import {selectedCustomerSelector} from "../store/slice/auth";

const initialFilters = () => {
    return [{
        type: FilterParamType.PICKUP_OR_DELIVER_TODAY,
        value: new Date(),
    }];
}

const ConsignmentsPage = () => {

    const [ errorMessage, setErrorMessage ] = useState<string | null>(null);
    const [ stateLoading, setStateLoading ] = useState<boolean>(false);
    const [ statePreviousLinks, setStatePreviousLinks ] = useState<string[]>([]);
    const [ stateCurrentLink, setStateCurrentLink ] = useState<string | null>(null);
    const [ stateNextLink, setStateNextLink ] = useState<string | null>(null);
    const [ stateConsignments, setStateConsignments ] = useState<CustomerAPIConsignmentResponse[]>([]);
    const [ stateExpandedIds, setStateExpandedIds ] = useState<Set<number>>(new Set());
    const [ stateFilters, setStateFilters ] = useState<Filter[]>(() => initialFilters());

    const topOfList = React.useRef<HTMLDivElement>(null);

    const selectedCustomer = useAppSelector(selectedCustomerSelector);

    const resetPagination = () => {
        setStatePreviousLinks([]);
        setStateCurrentLink(buildUrl(false));
        setStateNextLink(null);
    };

    const onFiltersChanged = (filters: Filter[]) => {
        if (filters !== stateFilters) {
            setStateFilters(filters);
            resetPagination();
        }
    };

    const scrollToTopOfList = () => {
        topOfList.current?.scrollIntoView({behavior: 'smooth'});
    }

    const gotoNext = () => {
        setStatePreviousLinks(prevState => {
            return [...prevState, stateCurrentLink!];
        });
        setStateCurrentLink(stateNextLink);
        setStateNextLink(null);
    };

    const gotoPrevious = () => {
        const previous = statePreviousLinks.pop();
        setStatePreviousLinks([...statePreviousLinks]) // we have popped the last one
        setStateCurrentLink(previous ?? buildUrl(false));
        setStateNextLink(null)
    };

    const buildUrl = (isCSV: boolean) => {

        let path = `${API.ORIGIN}/customer/v1/consignments`;
        const searchParams = new URLSearchParams();

        if (isCSV) {
            if (selectedCustomer && selectedCustomer.id) {
                searchParams.set("customerId", String(selectedCustomer.id));
            }
            path += '/csv';
        }

        searchParams.set("limit", "25");
        stateFilters.forEach(filter => {
            searchParams.set(filter.type.queryParam, filter.type.formatForQueryParam(filter.value));
        });
        const queryParams = searchParams.toString();
        if (queryParams) {
            return path + `?${queryParams}`
        }
        return path;
    };

    const loadDeliveries = (scroll = true) => {
        const url = stateCurrentLink;
        if (!url) {
            return
        }

        setStateLoading(true);

        axios.get<CustomerAPIConsignmentResponse[]>(url)
            .then((response) => {
                const nextLink = extractFirstHeaderLink(response, 'next');
                setStateConsignments(response.data);
                setStateNextLink((nextLink) ? nextLink.uri : null);
                if (scroll) {
                    scrollToTopOfList();
                }
            })
            .catch((error) => {
                axiosErrorHandler(error, errorMessage => setErrorMessage(errorMessage));
            })
            .finally(() => setStateLoading(false));
    };

    const handleToggleExpand = (id: number) => {
        setStateExpandedIds(prevState => {
            const copy = new Set(prevState);
            if (copy.has(id)) {
                copy.delete(id)
            } else {
                copy.add(id)
            }
            return copy
        })
    }

    useEffect(() => {
        if (!stateCurrentLink) {
            setStateCurrentLink(buildUrl(false));
        }
    }, [stateCurrentLink]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        resetPagination();
        loadDeliveries(false);
    }, [selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (stateCurrentLink) {
            loadDeliveries(false)
        }
    }, [stateCurrentLink]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setStateCurrentLink(buildUrl(false))
    }, [stateFilters]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Container>
            <Row className="justify-content-end align-items-center">
                <div className="px-1 col-6 col-sm-8">
                    {/* <h3 className="p-1 p-sm-3">Deliveries</h3> */}
                </div>
                <div className="px-1 col-6 col-sm-4">
                    <a className="btn btn-outline-dark float-end" href={`${buildUrl(true)}`} target="_blank" rel="noopener noreferrer">
                        CSV <FontAwesomeIcon icon={faDownload}/>
                    </a>
                    <button className="btn btn-outline-dark float-end me-2" onClick={() => loadDeliveries(false)}>
                        <FontAwesomeIcon icon={faRetweet}/>
                    </button>
                </div>
            </Row>

            <FilterForm
                onFiltersChanged={onFiltersChanged}
                filters={[
                    FilterParamType.CONSIGNMENT_REF_CONTAINS,
                    FilterParamType.CUSTOMER_REF_CONTAINS,
                    FilterParamType.PICKUP_OR_DELIVER_TODAY,
                    FilterParamType.COMPLETED_ON_OR_BEFORE,
                    FilterParamType.COMPLETED_ON_OR_AFTER
                ]}
                selectedFilter={
                    FilterParamType.PICKUP_OR_DELIVER_TODAY
                }
                initialFilters={
                    stateFilters
                }
            />

            {errorMessage &&
                <Alert variant="danger" onClose={() => setErrorMessage(null)} dismissible>
                    <Alert.Heading>Oh snap! You got an error!</Alert.Heading>
                    <p>{errorMessage}</p>
                </Alert>
            }

            <div ref={topOfList} />

            {stateLoading
                ? <div className="text-center"><Spinner animation={"border"} /></div>
                : <>
                    <div className="d-none d-xl-block">
                        <ConsignmentTable
                            consignments={stateConsignments}
                            expandedIds={stateExpandedIds}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                    <div className="d-xl-none d-lg-block">
                        <ConsignmentList
                            consignments={stateConsignments}
                            expandedIds={stateExpandedIds}
                            onToggleExpand={handleToggleExpand}
                        />
                    </div>
                </>
            }

            <Row>
                <div className="col-12 pt-3">
                    <button className="btn btn-outline-dark float-start" disabled={statePreviousLinks.length < 1} onClick={gotoPrevious}>
                        <FontAwesomeIcon icon={faArrowLeft}/> Previous
                    </button>
                    <button className="btn btn-outline-dark float-end" disabled={!stateNextLink} onClick={gotoNext}>
                        Next <FontAwesomeIcon icon={faArrowRight}/>
                    </button>
                </div>
            </Row>

        </Container>
    )

}

export default ConsignmentsPage;
